import { Hash } from 'lucide-react';
import {
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
} from '../../../../components/ui/context-menu';
import { useAppStore, type NumericGroupingMode } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { NUMERIC_GROUPING_OPTIONS } from './numericGroupingOptions';

type NumericGroupingMenuProps = {
  /** Extra classes for the trigger + items (Table Explorer menus use `text-xs`). */
  className?: string;
};

/**
 * PP-01 — "Number display" cell context-menu submenu, shared by BOTH results
 * grids (`CellContextMenu` for the Query Editor family; inline in
 * `TableExplorerGrid`'s cell menu). Radio items mirror the global
 * `numericGrouping` setting; choosing one writes the setting for every grid,
 * exactly like the Settings → UI row (`NumericGroupingSection`).
 *
 * Only mounted while a menu is open on a NUMERIC cell, so subscribing to the
 * store here never touches the grids' per-cell render paths.
 */
export function NumericGroupingMenu({ className }: NumericGroupingMenuProps) {
  const numericGrouping = useAppStore((state) => state.numericGrouping);
  const setNumericGrouping = useAppStore((state) => state.setNumericGrouping);
  const { track } = useAnalytics();

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger className={className} data-testid="context-menu-number-display">
        <Hash className="h-3.5 w-3.5 mr-2" />
        Number display
      </ContextMenuSubTrigger>
      <ContextMenuSubContent>
        <ContextMenuRadioGroup
          value={numericGrouping}
          onValueChange={(value) => {
            const next = value as NumericGroupingMode;
            if (next === numericGrouping) return;
            track('feature:numeric_grouping_changed', { from: numericGrouping, to: next, surface: 'context-menu' });
            setNumericGrouping(next);
          }}
        >
          {NUMERIC_GROUPING_OPTIONS.map((option) => (
            <ContextMenuRadioItem
              key={option.mode}
              value={option.mode}
              className={className}
              data-testid={`context-menu-number-display-${option.mode}`}
            >
              {option.label}
            </ContextMenuRadioItem>
          ))}
        </ContextMenuRadioGroup>
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}
